import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchoolIcon from '@mui/icons-material/School';
import BuildIcon from '@mui/icons-material/Build';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sessionAPI } from '../services/api';
import { useClearOnSiteChange, useCurrentSiteId } from '../hooks/useCurrentSiteId';
import SessionSelector from './SessionSelector';
import AppHeader from './AppHeader';

const SECTION_EXECUTIVE = {
  id: 0,
  title: 'Executive',
  subtitle: 'Enrollment and projected revenue',
  iconBg: '#F3E8FF',
  Icon: AssessmentIcon,
  roles: ['executive', 'finance', 'admin', 'superadmin'],
  items: [
    {
      title: 'Enrollment & funding',
      description: 'Program counts derived from the roster',
      path: '/reports/enrollment',
      roles: ['executive', 'finance', 'admin', 'superadmin'],
    },
    {
      title: 'Revenue from mandates',
      description: 'Mandated, expected, billed, and collected stay separate',
      path: '/reports/revenue',
      roles: ['executive', 'finance', 'admin', 'superadmin'],
    },
  ],
};

const SECTION_1 = {
  id: 1,
  title: 'Students & enrollment',
  subtitle: 'Records, roster, attendance, medical, incidents, parent packet',
  iconBg: '#E6F1FB',
  Icon: SchoolIcon,
  items: [
    { title: 'Students', description: 'Manage records and enrollment', path: '/students', notRoles: ['attendance'] },
    { title: 'Student roster', description: 'Alphabetical list, export CSV', path: '/students/roster-list', notRoles: ['attendance'] },
    { title: 'Attendance', description: 'Daily, late/early, absences', path: '/attendance', notRoles: ['teacher'] },
    {
      title: 'My classroom',
      description: 'Teacher attendance, newsletters, parent messages',
      path: '/teacher',
      roles: ['teacher', 'admin', 'superadmin'],
    },
    {
      title: 'Therapy schedule',
      description: 'Daily sessions, makeups, attendance alerts',
      path: '/therapy/schedule',
      roles: ['therapist', 'admin', 'superadmin', 'editor'],
    },
    {
      title: 'Master therapy schedule',
      description: 'Assign therapists, view caseloads',
      path: '/therapy/master-schedule',
      roles: ['admin', 'superadmin', 'editor'],
    },
    {
      title: 'Classrooms',
      description: 'Room setup, ratios, SEDCAR waivers',
      path: '/classrooms',
      roles: ['admin', 'superadmin', 'editor', 'viewer'],
    },
    {
      title: 'Class rosters',
      description: 'Move or add children between classes',
      path: '/classrooms/roster',
      roles: ['admin', 'superadmin', 'editor', 'roster'],
    },
    { title: 'Reports', description: 'Caseload, class roster, medical, staff, nurse', path: '/reports', notRoles: ['attendance', 'finance', 'executive'] },
    { title: 'Program grid', description: 'Every child on one table. Filters replace Query Design.', path: '/program', notRoles: ['attendance', 'nurse', 'finance', 'executive', 'teacher', 'therapist', 'transportation', 'roster'] },
    { title: 'Medical due reports', description: 'Medicals and therapy scripts, export CSV', path: '/reports/medical-due', notRoles: ['attendance'] },
    { title: 'Term rollover', description: 'Copy a session into summer. Consents are not copied.', path: '/sessions/rollover', roles: ['admin', 'superadmin', 'editor'] },
    {
      title: 'Parent packet dashboard',
      description: 'Packet status by student, complete/in-progress',
      path: '/admin/packet-dashboard',
      roles: ['admin', 'superadmin', 'editor'],
    },
    {
      title: 'Packet recipients',
      description: 'Who was sent a packet, contact data quality, CSV',
      path: '/admin/packet-recipients',
      roles: ['admin', 'superadmin', 'editor'],
    },
    {
      title: 'Paper intake queue',
      description: 'Assign parent/physician emails that need a student match',
      path: '/admin/medform-pending',
      roles: ['admin', 'superadmin', 'editor'],
    },
    { title: 'Incident log', description: 'Log and filter incidents', path: '/incidents', notRoles: ['attendance'] },
    { title: 'Parent packet', description: 'Session setup, send packets', path: '/admin/school-session-setup', adminOnly: true },
  ],
};

const SECTION_2 = {
  id: 2,
  title: 'Operations',
  subtitle: 'Transportation, employees, payroll',
  iconBg: '#E1F5EE',
  Icon: BuildIcon,
  items: [
    { title: 'Transportation', description: 'TRF, send to parent', path: '/transportation', roles: ['superadmin', 'admin', 'editor', 'transportation'] },
    { title: 'Bus Roster', description: 'Bus info by student', path: '/transportation/bussing', roles: ['superadmin', 'admin', 'editor', 'transportation'] },
    { title: 'Bus sheet', description: 'Weekly route sheet for the driver', path: '/transportation/bus-sheet', roles: ['superadmin', 'admin', 'editor', 'transportation'] },
    { title: 'Employees', description: 'Records and assignments', path: '/employees', notRoles: ['attendance', 'finance'] },
    { title: 'Payroll allocation', description: 'PCS cost allocation maps', path: '/payroll', roles: ['superadmin', 'finance'] },
  ],
};

const SECTION_COMMUNICATIONS = {
  id: 3,
  title: 'Communications',
  subtitle: 'Parent messaging and campaigns',
  iconBg: '#E8EAF6',
  Icon: CampaignOutlinedIcon,
  roles: ['superadmin', 'admin', 'editor', 'viewer'],
  items: [
    {
      title: 'Send Message',
      description: 'SMS and voice (TTS) to parents',
      path: '/communications/new',
      roles: ['superadmin', 'admin', 'editor', 'viewer'],
    },
    {
      title: 'Campaigns',
      description: 'View sent messages and delivery status',
      path: '/communications',
      roles: ['superadmin', 'admin', 'editor', 'viewer'],
    },
    {
      title: 'Emergency Broadcast',
      description: 'One-tap emergency alert to all parents',
      path: '/communications?emergency=1',
      adminOnly: true,
    },
  ],
};

const SECTION_3 = {
  id: 4,
  title: 'Admin',
  subtitle: 'Sessions, users, security, compliance',
  iconBg: '#F1EFE8',
  Icon: AdminPanelSettingsIcon,
  items: [
    { title: 'Sessions', description: 'Create, copy students', path: '/admin/sessions', adminOnly: true },
    { title: 'School session setup', description: 'Calendar, off days, schedule', path: '/admin/school-session-setup', adminOnly: true },
    { title: 'Packet Templates', description: 'Edit parent packet form content', path: '/admin/packet-templates', adminOnly: true },
    { title: 'User management', description: 'Accounts, roles, sites', path: '/admin/users', adminOnly: true },
    { title: 'Security (MFA)', description: 'Two-factor authentication', path: '/mfa/setup', notRoles: ['attendance'] },
    { title: 'Compliance reports', description: 'Access logs, disclosures', path: '/compliance', adminOnly: true },
  ],
};

function filterItems(items, user) {
  if (!items) return [];
  const role = user?.role;
  const limited = role === 'finance' || role === 'executive';
  const nursePaths = ['/students', '/reports', '/reports/medical-due', '/reports/nurse-medications', '/reports/physician-list', '/mfa/setup'];
  return items.filter((item) => {
    if (role === 'nurse') {
      return nursePaths.some((path) => item.path === path);
    }
    if (item.adminOnly && role !== 'superadmin' && role !== 'admin') return false;
    if (item.notRoles && item.notRoles.includes(role)) return false;
    if (item.roles && !item.roles.includes(role)) return false;
    if (limited && !item.roles) return false;
    return true;
  });
}

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const siteId = useCurrentSiteId();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  useClearOnSiteChange(siteId, () => {
    setSessions([]);
    setCurrentSession(null);
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sessRes = await sessionAPI.getSessions();
        const list = sessRes.data.results || sessRes.data || [];
        if (cancelled) return;
        setSessions(list);

        let current = null;
        try {
          const curRes = await sessionAPI.getCurrentSession();
          current = curRes.data;
        } catch (_) {}
        if (cancelled) return;
        setCurrentSession(current);

        const leavesSessionAlone = user?.role === 'executive' || user?.role === 'finance';
        if (list.length > 0 && !current?.id && !leavesSessionAlone) {
          try {
            await sessionAPI.setActiveSession(list[0].id);
            const curRes = await sessionAPI.getCurrentSession();
            if (!cancelled) setCurrentSession(curRes.data);
          } catch (err) {
            console.error('Error auto-setting session:', err);
          }
        }
      } catch (error) {
        console.error('Error loading sessions:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId, user?.role]);

  const loadCurrentSession = async () => {
    try {
      const response = await sessionAPI.getCurrentSession();
      setCurrentSession(response.data);
    } catch (error) {
      setCurrentSession(null);
    }
  };

  const handleSessionChange = async (sessionId) => {
    if (sessionId === '') return;
    try {
      await sessionAPI.setActiveSession(sessionId);
      await loadCurrentSession();
    } catch (error) {
      console.error('Error setting active session:', error);
    }
  };

  const handleSectionClick = (sectionId) => {
    setExpandedSection((prev) => (prev === sectionId ? null : sectionId));
  };

  const sections = [SECTION_EXECUTIVE, SECTION_1, SECTION_2, SECTION_COMMUNICATIONS, SECTION_3].filter((s) => {
    if (s.adminOnly && user?.role !== 'superadmin' && user?.role !== 'admin') return false;
    if (s.roles && user?.role && !s.roles.includes(user.role)) return false;
    return true;
  });

  if (user?.role === 'teacher') {
    return <Navigate to="/teacher/attendance" replace />;
  }

  if (user?.role === 'therapist') {
    return <Navigate to="/therapy/schedule" replace />;
  }

  const orgWideReader = user?.role === 'executive' || user?.role === 'finance';

  return (
    <Box>
      <AppHeader
        rightContent={
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user?.email} ({user?.role})
            </Typography>
            <Button color="inherit" onClick={logout} sx={{ color: '#fff' }}>
              Logout
            </Button>
          </Box>
        }
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {!orgWideReader && (
          <Box sx={{ mb: 3 }}>
            <SessionSelector
              sessions={sessions}
              currentSession={currentSession}
              onSessionChange={handleSessionChange}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {sections.map((section) => {
            const filteredItems = filterItems(section.items, user);
            if (filteredItems.length === 0) return null;
            const isExpanded = expandedSection === section.id;
            const Icon = section.Icon;
            return (
              <Box key={section.id}>
                <Box
                  component="button"
                  onClick={() => handleSectionClick(section.id)}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    textAlign: 'left',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        bgcolor: section.iconBg,
                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                    >
                      <Icon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
                        {section.title}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {section.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                  <ExpandMoreIcon
                    sx={{
                      flexShrink: 0,
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }}
                  />
                </Box>
                <Collapse in={isExpanded}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 1.5,
                      pt: 1.5,
                      pb: 1,
                      pl: 1,
                      pr: 1,
                    }}
                  >
                    {filteredItems.map((item) => (
                      <Box
                        key={`${item.title}-${item.path}`}
                        component="button"
                        onClick={() => navigate(item.path)}
                        sx={{
                          p: 1.5,
                          textAlign: 'left',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: '#fafafa',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#fff' },
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 500, display: 'block' }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', display: 'block' }}>
                          {item.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
